import Vue from "vue";
import axios from "axios";
import VueAxios from "vue-axios";
import $store from "../store";
import toLogin from "./login";
import { decodeMojibakeDeep } from "./mojibake";

Vue.use(VueAxios, axios);

const instance = axios.create({
  baseURL: process.env.VUE_APP_BASE_URL,
  timeout: 20000,
});

const defaultOpt = { login: true };

function getToken() {
  return $store.state.app.token || "";
}

function isDebugToken(token = getToken()) {
  return typeof token === "string" && token.startsWith("debug_");
}

function buildErrorPayload(msg, extra = {}) {
  const decodedMsg = decodeMojibakeDeep(msg);
  const decodedData = decodeMojibakeDeep(extra.data || {});
  return {
    ...extra,
    msg: decodedMsg,
    data: { msg: decodedMsg, ...decodedData },
  };
}

function rejectUnauthorized(payload = {}) {
  const debug = isDebugToken();
  return Promise.reject(
    buildErrorPayload(payload.msg || "Unauthorized", {
      res: payload.res,
      data: payload.data,
      toLogin: !debug,
      debug,
    })
  );
}

instance.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.data?.status ?? error?.response?.status;

    if (error?.toString?.() === "Error: Network Error") {
      return Promise.reject(
        buildErrorPayload("Network error: backend API unreachable", {
          toLogin: false,
          debug: isDebugToken(),
        })
      );
    }

    if (status === 401) {
      if (!isDebugToken()) {
        toLogin();
      }
      return rejectUnauthorized({ msg: "Unauthorized", res: error?.response });
    }

    return Promise.reject(
      buildErrorPayload(
        error?.response?.data?.msg || error?.response?.data?.message || "Request failed",
        {
          res: error?.response,
          data: error?.response?.data,
        }
      )
    );
  }
);

function baseRequest(options) {
  const token = getToken();
  const headers = options.headers || {};
  headers.Authorization = `Bearer ${token}`;
  options.headers = headers;

  if (options.login && !token) {
    toLogin();
    return Promise.reject(buildErrorPayload("Unauthorized", { toLogin: true }));
  }

  return instance(options).then((res) => {
    const data = decodeMojibakeDeep(res.data || {});

    if (res.status < 200 || res.status >= 300) {
      return Promise.reject({ msg: "Request failed", res, data });
    }

    if (data.errno === 0) {
      return Promise.resolve(data);
    }

    if ([401, 403].indexOf(data.status) !== -1) {
      if (!isDebugToken(token)) {
        toLogin();
      }
      return rejectUnauthorized({ msg: data.msg || "Unauthorized", res, data });
    }

    if (data.status === 200) {
      return Promise.resolve(data);
    }

    if (data.status === 5101) {
      return Promise.reject({ msg: data.msg || "Request failed", res, data });
    }

    return Promise.reject({ msg: data.msg || "Request failed", res, data });
  });
}

const request = ["post", "put", "patch"].reduce((result, method) => {
  result[method] = (url, data = {}, options = {}) =>
    baseRequest(Object.assign({ url, data, method }, defaultOpt, options));
  return result;
}, {});

["get", "delete", "head"].forEach((method) => {
  request[method] = (url, params = {}, options = {}) =>
    baseRequest(Object.assign({ url, params, method }, defaultOpt, options));
});

export default request;
